interface EventEntry { event: string; eventCount: number; users: number }
interface BreakdownEntry extends EventEntry { value: string }
interface ScreenEntry { screenName: string; platform: string; appVersion: string; eventCount: number; users: number }
export interface GA4EventReportsData {
  unavailable?: string[];
  screenDimension?: string;
  screenViews?: ScreenEntry[];
  funnelEvents?: EventEntry[];
  funnelBreakdowns?: Record<'tier' | 'source' | 'result', BreakdownEntry[]>;
}

const tableStyle = { width: '100%', textAlign: 'left' as const, fontSize: 12, borderSpacing: '8px' };

export default function GA4EventReports({ data }: { data: GA4EventReportsData }) {
  const unavailable = (key: string) => data.unavailable?.includes(key);
  const screens = data.screenViews || [];
  const views = screens.reduce((sum, row) => sum + row.eventCount, 0);
  const missing = screens.reduce((sum, row) => sum + (row.screenName === '(not set)' ? row.eventCount : 0), 0);
  return (
    <section style={{ color: 'var(--color-text-secondary)', marginBottom: 24 }}>
      <h4>Screen names — Last 30 Days</h4>
      <p style={{ fontSize: 12 }}>Android/iOS screen_view events. Dimension: {data.screenDimension || 'unifiedScreenName'} (screen name). Users are distinct per row and cannot be added across rows.</p>
      {unavailable('screenViews') || !data.screenViews ? <p>Screen names unavailable</p> : <>
        <p>{missing.toLocaleString()} of {views.toLocaleString()} screen_view events have (not set){views > 0 ? ` (${(100 * missing / views).toFixed(1)}%)` : ''}.</p>
        {screens.length === 0 ? <p>No screen_view events in this window.</p> : <>
          <table style={tableStyle}>
            <thead><tr><th>Screen name</th><th>Platform</th><th>App version</th><th>Events</th><th>Users</th></tr></thead>
            <tbody>{screens.slice(0, 100).map((row, i) => <tr key={i}><td>{row.screenName}</td><td>{row.platform}</td><td>{row.appVersion}</td><td>{row.eventCount.toLocaleString()}</td><td>{row.users.toLocaleString()}</td></tr>)}</tbody>
          </table>
          {screens.length > 100 ? <p>Showing the first 100 rows; missing-name totals include all {screens.length.toLocaleString()} rows.</p> : null}
        </>}
      </>}
      <h4>App funnel events — Last 30 Days</h4>
      <p style={{ fontSize: 12 }}>Event counts and independent distinct users per event in the window. These are not an ordered signup cohort. Base totals use built-in dimensions only.</p>
      {unavailable('funnelEvents') || !data.funnelEvents ? <p>Funnel events unavailable</p> : <table style={tableStyle}>
        <thead><tr><th>Event</th><th>Events</th><th>Users</th></tr></thead>
        <tbody>{data.funnelEvents.map((row) => <tr key={row.event}><td>{row.event}</td><td>{row.eventCount.toLocaleString()}</td><td>{row.users.toLocaleString()}</td></tr>)}</tbody>
      </table>}
      {(['tier', 'source', 'result'] as const).map((dimension) => <details key={dimension} style={{ marginTop: 12 }}>
        <summary>Breakdown by {dimension} (customEvent:{dimension})</summary>
        {unavailable(`funnelBreakdowns.${dimension}`) || !data.funnelBreakdowns ? <p>{dimension} breakdown unavailable. Verify the event-scoped custom dimension registration and GA access.</p> : <>
          <p style={{ fontSize: 12 }}>Users can occur in multiple values. (not set) means no reported value for this dimension; not every parameter applies to every event.</p>
          {data.funnelBreakdowns[dimension].length === 0 ? <p>No events in this window.</p> : <table style={tableStyle}>
            <thead><tr><th>Event</th><th>{dimension}</th><th>Events</th><th>Users</th></tr></thead>
            <tbody>{data.funnelBreakdowns[dimension].map((row, i) => <tr key={i}><td>{row.event}</td><td>{row.value}</td><td>{row.eventCount.toLocaleString()}</td><td>{row.users.toLocaleString()}</td></tr>)}</tbody>
          </table>}
        </>}
      </details>)}
    </section>
  );
}
